// File: frontend/src/context/PermissionsContext.js
//
// Provides the logged-in user's effective permissions to the whole admin app.
// Fetched from GET /api/users/me/permissions (server-authoritative). Frontend
// gating is a UX convenience only — the backend independently enforces every
// sensitive route, so hiding a button never grants or protects data by itself.
//
// IMPORTANT (regression fix): a FAILED permissions fetch (CORS / network / 5xx /
// stale-after-login) must NEVER be interpreted as "this user has no access".
// Only a SUCCESSFUL load that says a page isn't allowed is a real denial. So we
// expose an explicit `error` ("auth" | "network" | null) and `loadedOnce`, and
// we refetch when the auth state changes (login without a full page reload).

import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { permissionsService, authService } from "../services/api";
import { getAccountIdentity, getAuthEpoch, isCurrentEpoch } from "../utils/accountScope";

const PermissionsContext = createContext(null);

const EMPTY = {
  loading: true,
  loadedOnce: false,
  error: null,              // null | "auth" | "network"
  isAdmin: false,
  isOwner: false,
  roleLabel: "",
  permissions: new Set(),
  allowedPages: new Set(),
};

// Classify a failed permissions fetch WITHOUT ever calling it a permission denial.
//   401 → the session is expired/invalid → "auth" (app redirects to login)
//   anything else (no response = CORS/network, 5xx, even a stray 403 on /me) →
//   "network" (retryable) — we could not determine access, so we must not deny.
function classifyError(err) {
  const status = err?.response?.status;
  if (status === 401) return "auth";
  return "network";
}

export function PermissionsProvider({ children }) {
  const [state, setState] = useState(EMPTY);
  const reqRef = useRef(0);
  // The account these permissions belong to. Used to detect an identity change
  // so the previous account's grants can be dropped BEFORE any render.
  const identityRef = useRef(null);

  const load = useCallback(async () => {
    // No logged-in user → nothing to fetch (public pages). This is a definitive,
    // successful "empty" state, not an error.
    if (!authService.getCurrentUser()) {
      identityRef.current = null;
      setState({ ...EMPTY, loading: false, loadedOnce: true, permissions: new Set(), allowedPages: new Set() });
      return;
    }

    // IDENTITY CHANGE ⇒ HARD RESET. Previously a refetch kept the old
    // permissions/allowedPages in state "so a background refresh never
    // downgrades a working session". That is right for a refresh of the SAME
    // account and wrong across accounts: RequirePage renders immediately for an
    // affirmatively-allowed page, so Account A's grants could authorize Account
    // B's first render. Empty grants + loading:true means the gate waits.
    const { tenantId, userId } = getAccountIdentity();
    const identity = tenantId && userId ? `${tenantId}:${userId}` : null;
    const identityChanged = identity !== identityRef.current;
    identityRef.current = identity;

    const myReq = ++reqRef.current;
    const startEpoch = getAuthEpoch();
    setState((s) => (identityChanged
      ? { ...EMPTY, loading: true, loadedOnce: false }
      : { ...s, loading: true }));
    try {
      const data = await permissionsService.me();
      if (myReq !== reqRef.current) return;
      // Belongs to a previous account — never apply it.
      if (!isCurrentEpoch(startEpoch)) return;
      if (data?.success) {
        const isOwner = !!data.is_owner;
        const isAdmin = isOwner || !!data.is_admin || (Array.isArray(data.permissions) && data.permissions.includes("*"));
        setState({
          loading: false,
          loadedOnce: true,
          error: null,
          isAdmin,
          isOwner,
          roleLabel: data.role_label || (isOwner ? "Owner" : isAdmin ? "Admin" : ""),
          permissions: new Set(isAdmin ? [] : (data.permissions || [])),
          allowedPages: new Set(data.allowed_pages || []),
        });
      } else {
        // A 200 with success:false means the server couldn't produce permissions —
        // treat as a load error (retryable), NOT a denial. Preserve any prior good
        // state so a transient blip after a successful load doesn't nuke the UI.
        console.warn("[permissions] /me/permissions returned success:false — treating as load error, not denial");
        // Preserve prior grants ONLY within the same account (see above).
        setState((s) => (identityChanged
          ? { ...EMPTY, loading: false, loadedOnce: true, error: "network" }
          : { ...s, loading: false, error: "network" }));
      }
    } catch (err) {
      if (myReq !== reqRef.current) return;
      if (!isCurrentEpoch(startEpoch)) return;
      const kind = classifyError(err);
      console.warn(`[permissions] load failed (${kind}) status=${err?.response?.status || "none"} — NOT treating as access-denied`);
      // Preserve prior successful permissions so a background refetch failure
      // never downgrades a working session — but ONLY within the same account.
      setState((s) => (identityChanged
        ? { ...EMPTY, loading: false, loadedOnce: true, error: kind }
        : { ...s, loading: false, error: kind }));
    }
  }, []);

  // Initial load.
  useEffect(() => { load(); }, [load]);

  // Refetch whenever the auth state changes. authService dispatches
  // `pesafii:auth-changed` on login/logout, and the browser fires `storage` when
  // another tab logs in/out. This fixes the "logged in again but the dashboard
  // shows access-denied until I refresh" case (login is an SPA navigation, so the
  // provider never remounts on its own).
  useEffect(() => {
    const onAuthChanged = () => load();
    const onStorage = (e) => { if (!e.key || e.key === "token" || e.key === "user") load(); };
    window.addEventListener("pesafii:auth-changed", onAuthChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pesafii:auth-changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  const value = useMemo(() => {
    const can = (key) => {
      if (!key) return true;
      if (state.isAdmin) return true;
      return state.permissions.has(key);
    };
    const canPage = (path) => {
      if (state.isAdmin) return true;
      // Normalise nested paths (e.g. "/sales/agents" → "/sales").
      const base = "/" + String(path || "").split("/").filter(Boolean)[0];
      return state.allowedPages.has(base);
    };
    return { ...state, can, canPage, reload: load };
  }, [state, load]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  // Safe default when used outside the provider (e.g. public pages).
  if (!ctx) {
    return {
      loading: false, loadedOnce: true, error: null,
      isAdmin: true, isOwner: false, roleLabel: "", permissions: new Set(), allowedPages: new Set(),
      can: () => true, canPage: () => true, reload: () => {},
    };
  }
  return ctx;
}

export default PermissionsContext;
